<template>
  <div class="research-strength-page">
    <!-- 内页头 -->
    <inner-hero
      title="科研与产业实力"
      parent-label="企业概况"
      background-image="/images/company/strength/pilot-base.jpg"
      description="从科研创新、中试验证到成果转化与产品产业化，呈现可回溯的能力路径。"
    />

    <!-- 能力链 -->
    <section class="section" data-od-id="capability-chain">
      <div class="page-container">
        <div class="research-section-head">
          <div>
            <span class="research-kicker">Innovation Pipeline</span>
            <h2>从科研创新到产业服务的完整路径</h2>
          </div>
          <p>
            围绕热带特色作物，贯通研究、验证、转化、产业化与技术服务，让科研能力真正抵达生产现场。
          </p>
        </div>
        <div
          ref="capabilityTrack"
          class="capability-tabs"
          role="tablist"
          aria-label="科研与产业能力链"
        >
          <button
            v-for="(tab, i) in capabilityTabs"
            :key="tab.key"
            :id="`capability-tab-${tab.key}`"
            class="capability-tab"
            :class="{ active: i === activeTab }"
            type="button"
            role="tab"
            :aria-selected="i === activeTab"
            aria-controls="capability-panel"
            :tabindex="i === activeTab ? 0 : -1"
            @click="selectCapability(i)"
            @keydown="onCapabilityKeydown($event, i)"
          >
            <span class="capability-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path :d="tab.path" />
              </svg>
            </span>
            <strong>{{ tab.label }}</strong>
            <small>{{ tab.en }}</small>
          </button>
        </div>
        <div
          id="capability-panel"
          class="capability-panel"
          :class="{ 'is-switching': isCapabilitySwitching }"
          role="tabpanel"
          :aria-labelledby="`capability-tab-${activeCapability.key}`"
          aria-live="polite"
        >
          <div class="capability-panel-media">
            <img
              :key="activeCapability.key"
              :src="activeCapability.image"
              :alt="activeCapability.alt"
              width="1600"
              height="992"
              decoding="async"
              @load="onImgLoad"
              @error="onImgError"
            />
          </div>
          <div class="capability-panel-copy">
            <span>SELECTED · {{ String(activeTab + 1).padStart(2, '0') }}</span>
            <h3>{{ activeCapability.label }}</h3>
            <p>{{ activeCapability.copy }}</p>
            <div class="capability-panel-tags">
              <span v-for="tag in activeCapability.tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>

        <!-- 移动端：轮播卡片 -->
        <ui-expandable-gallery
          :items="capabilityTabs"
          :default-index="0"
          class="capability-carousel"
          role="list"
          aria-label="科研与产业能力链"
        >
          <template #default="{ item, active, index }">
            <div
              class="capability-carousel-card"
              :class="{ active }"
              @click="selectCapability(index)"
            >
              <div class="capability-carousel-media">
                <img
                  :src="item.image"
                  :alt="item.alt"
                  width="1600"
                  height="992"
                  decoding="async"
                  @load="onImgLoad"
                  @error="onImgError"
                />
              </div>
              <div class="capability-carousel-copy">
                <span class="capability-carousel-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path :d="item.path" />
                  </svg>
                </span>
                <div class="capability-carousel-head">
                  <strong>{{ item.label }}</strong>
                  <small>{{ item.en }}</small>
                </div>
                <p>{{ item.copy }}</p>
                <div class="capability-panel-tags">
                  <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </template>
        </ui-expandable-gallery>
      </div>
    </section>

    <!-- 科研平台与实验条件 -->
    <section class="section platform-section" data-od-id="research-platforms">
      <div class="page-container">
        <div class="research-section-head">
          <div>
            <span class="research-kicker">Research Platform</span>
            <h2>科研平台与实验条件</h2>
          </div>
          <p>以真实科研场景作为视觉主体，围绕"研究什么、由谁研究、如何验证"建立科研可信度。</p>
        </div>
        <div class="platform-layout">
          <div class="platform-visual media">
            <img
              src="/images/company/strength/platform-lab.jpg"
              alt="海南兴科研发实验与检测场景"
              width="1200"
              height="430"
              loading="lazy"
              decoding="async"
              @load="onImgLoad"
              @error="onImgError"
            />
            <span class="media-fallback">图片加载失败，科研平台文字仍可阅读</span>
            <div class="platform-caption">
              <b>让科研价值抵达产业现场</b>
              <span>覆盖研究、工艺验证、产品开发与成果应用</span>
            </div>
          </div>
          <div class="platform-list">
            <article v-for="item in platformItems" :key="item.title" class="platform-item">
              <span class="platform-item-icon">
                <svg viewBox="0 0 24 24"><path :d="item.path" /></svg>
              </span>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.desc }}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- 中试验证 -->
    <section class="pilot-section" data-od-id="pilot-production">
      <div class="page-container">
        <div class="pilot-copy">
          <span class="research-kicker">Pilot Verification</span>
          <h2>把实验成果推进到规模化生产验证</h2>
          <p>
            通过中试基地、生产线和研发生产设备，完成工艺放大、稳定性验证与质量衔接，为产业化落地降低不确定性。
          </p>
        </div>
        <div class="pilot-metrics" aria-label="中试基地关键数据">
          <article class="pilot-metric" aria-label="15,000+ 平方米中试基地">
            <b>
              15,000
              <sup>+</sup>
            </b>
            <span>平方米中试基地</span>
          </article>
          <article class="pilot-metric">
            <b>8</b>
            <span>条中试生产线</span>
          </article>
          <article class="pilot-metric" aria-label="700+ 台（套）研发生产设备">
            <b>
              700
              <sup>+</sup>
            </b>
            <span>台（套）研发生产设备</span>
          </article>
        </div>
      </div>
    </section>

    <!-- 成果、专利与产业应用 -->
    <section class="section" data-od-id="achievements">
      <div class="page-container">
        <div class="research-section-head">
          <div>
            <span class="research-kicker">Achievements</span>
            <h2>成果、专利与产业应用</h2>
          </div>
          <p>
            以成果转化为核心，把专利、奖励与产业应用放进同一能力语境，形成从技术到产品的可信证明。
          </p>
        </div>
        <div class="achievement-layout">
          <div class="achievement-visual media">
            <img
              src="/images/company/strength/industrial-application.webp"
              alt="海南兴科生产与产业化应用场景"
              width="1600"
              height="975"
              loading="lazy"
              decoding="async"
              @load="onImgLoad"
              @error="onImgError"
            />
            <span class="media-fallback">图片加载失败，成果信息仍可阅读</span>
            <div class="achievement-visual-copy">
              <span>INDUSTRIAL APPLICATION</span>
              <h3>从技术成果走向可应用的产品方案</h3>
              <p>形成覆盖原料研究、加工工艺、产品开发与产业服务的成果体系。</p>
            </div>
          </div>
          <div class="achievement-cards">
            <article v-for="card in achievementCards" :key="card.num" class="achievement-card">
              <span class="achievement-icon">
                <svg viewBox="0 0 24 24"><path :d="card.path" /></svg>
              </span>
              <b :class="{ text: card.isText }">
                {{ card.num }}
                <sup v-if="card.suffix">{{ card.suffix }}</sup>
              </b>
              <span v-if="card.label">{{ card.label }}</span>
              <p v-if="card.desc">{{ card.desc }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- 产业服务 -->
    <section class="section service-section" data-od-id="quality-industrialization">
      <div class="page-container">
        <div class="service-layout">
          <div class="service-copy">
            <span class="research-kicker">Industrial Service</span>
            <h2>以质量体系连接研发、生产与服务</h2>
            <p>
              质量管理贯穿能力链，支撑产品研发、工艺优化、中试验证、定制生产与技术服务，让科研成果以稳定方式进入产业应用。
            </p>
            <div class="service-tags">
              <span v-for="tag in serviceTags" :key="tag">{{ tag }}</span>
            </div>
          </div>
          <div class="service-media media">
            <img
              src="/images/company/strength/research-building.jpg"
              alt="海南兴科综合科研与产业服务场景"
              width="1600"
              height="1066"
              loading="lazy"
              decoding="async"
              @load="onImgLoad"
              @error="onImgError"
            />
            <span class="media-fallback">图片加载失败，产业服务信息仍可阅读</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系行动区 -->
    <contact-action />
  </div>
</template>
<script setup lang="ts">
  import {
    createBreadcrumbJsonLd,
    createSiteUrl,
    getPageSeo,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  /**
   * 科研与产业实力页静态数据
   * 文案取自高保真原型，正式上线前须以客户确认资料为准
   */
  const seo = getPageSeo('company-strength')
  const siteUrl = getSiteUrl()

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'article',
    ogImage: siteSeo.ogImage,
    twitterTitle: seo.title,
    twitterDescription: seo.description,
    twitterImage: siteSeo.ogImage,
  })

  useHead({
    meta: [{ name: 'keywords', content: seo.keywords?.join(', ') }],
    link: [{ rel: 'canonical', href: createSiteUrl('/company/strength', siteUrl) }],
  })
  useJsonLd(
    createBreadcrumbJsonLd(
      [
        { name: '首页', path: '/' },
        { name: '科研与产业实力', path: '/company/strength' },
      ],
      siteUrl
    )
  )

  const activeTab = shallowRef(0)
  const isCapabilitySwitching = shallowRef(false)
  const capabilityTrack = useTemplateRef<HTMLElement>('capabilityTrack')
  let capabilityTimer: number | undefined

  /** 图片加载失败时显示所属媒体容器的文本占位。 */
  function onImgError(e: Event) {
    const img = e.target as HTMLImageElement
    img.classList.add('is-error')
    img.closest('.media, .capability-panel-media')?.classList.add('is-error')
  }

  /** 图片重新加载成功时清除可能残留的失败状态。 */
  function onImgLoad(e: Event) {
    const img = e.target as HTMLImageElement
    img.classList.remove('is-error')
    img.closest('.media, .capability-panel-media')?.classList.remove('is-error')
  }

  const capabilityTabs = [
    {
      key: 'research',
      label: '科研创新',
      en: 'RESEARCH',
      image: '/images/company/strength/capability-research.jpg',
      alt: '科研人员在实验室开展热带作物研究',
      copy: '围绕咖啡、可可等热带特色作物，聚焦资源评价、精深加工、活性成分与产品开发，持续积累原创科研能力。',
      tags: ['特色作物', '精深加工', '产品研发'],
      path: 'M10 2v7.31M14 9.3V1.99M8.5 2h7M14 9.3 19.57 19a2 2 0 0 1-1.73 3H6.16a2 2 0 0 1-1.73-3L10 9.3M6.5 17h11',
    },
    {
      key: 'pilot',
      label: '中试验证',
      en: 'PILOT TEST',
      image: '/images/company/strength/pilot-base.jpg',
      alt: '海南兴科中试基地与生产验证场景',
      copy: '通过工艺放大、设备适配与稳定性验证，把实验室成果推进到可评估、可复现的生产环节。',
      tags: ['工艺放大', '稳定验证', '质量衔接'],
      path: 'M12 16v5M16 14v7M20 10v11m2-18-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.708 0L2 15M4 18v3M8 14v7',
    },
    {
      key: 'transfer',
      label: '成果转化',
      en: 'TRANSFER',
      image: '/images/company/strength/capability-transfer.webp',
      alt: '科研团队开展热带作物成果转化与中试协同',
      copy: '连接科研成果与产业需求，围绕技术成熟度、应用场景和产品方向形成可落地的转化方案。',
      tags: ['技术成果', '应用场景', '转化方案'],
      path: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
    },
    {
      key: 'industry',
      label: '产品产业化',
      en: 'INDUSTRY',
      image: '/images/company/strength/capability-industry.webp',
      alt: '热带特色作物产品产业化与质量检验场景',
      copy: '以研发、中试和质量能力支撑产品开发、定制生产与规模化应用，推动成果形成稳定产品。',
      tags: ['产品开发', '定制生产', '规模应用'],
      path: 'M12 16h.01M16 16h.01M3 21h18M5 21V10l5 4v-4l5 4h4M19 21V8l-3-2v8',
    },
    {
      key: 'service',
      label: '技术服务',
      en: 'SERVICE',
      image: '/images/company/strength/capability-service.webp',
      alt: '海南兴科专业团队提供技术服务',
      copy: '面向科研协同、工艺优化、中试验证和成果应用等需求，提供与项目阶段匹配的技术服务。',
      tags: ['科研协同', '工艺优化', '产业服务'],
      path: 'M12 12h.01M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M22 13a18.15 18.15 0 0 1-20 0M4 6h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2',
    },
  ]

  const activeCapability = computed(() => capabilityTabs[activeTab.value]!)

  /** 切换能力阶段，并在移动端将选中项滚动至轨道中央。 */
  async function selectCapability(index: number, shouldFocus = false) {
    const nextIndex = (index + capabilityTabs.length) % capabilityTabs.length
    isCapabilitySwitching.value = true
    activeTab.value = nextIndex

    if (capabilityTimer) window.clearTimeout(capabilityTimer)
    capabilityTimer = window.setTimeout(() => {
      isCapabilitySwitching.value = false
      capabilityTimer = undefined
    }, 140)

    await nextTick()
    const tab =
      capabilityTrack.value?.querySelectorAll<HTMLButtonElement>('.capability-tab')[nextIndex]
    if (!tab) return
    if (shouldFocus) tab.focus()
    if (
      !capabilityTrack.value ||
      capabilityTrack.value.scrollWidth <= capabilityTrack.value.clientWidth
    )
      return

    tab.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }

  /** 支持能力 Tab 的方向键及 Home、End 键导航。 */
  function onCapabilityKeydown(event: KeyboardEvent, index: number) {
    const keyOffsets: Partial<Record<KeyboardEvent['key'], number>> = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
    }
    const isBoundaryKey = event.key === 'Home' || event.key === 'End'
    const offset = keyOffsets[event.key]
    if (offset === undefined && !isBoundaryKey) return

    event.preventDefault()
    let nextIndex = index + (offset ?? 0)
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = capabilityTabs.length - 1
    selectCapability(nextIndex, true)
  }

  onBeforeUnmount(() => {
    if (capabilityTimer) window.clearTimeout(capabilityTimer)
  })

  const platformItems = [
    {
      title: '特色作物研究',
      desc: '聚焦咖啡、可可等热带作物的资源评价、加工与应用研究。',
      path: 'M6 18h8M3 22h18M14 22a7 7 0 1 0 0-14h-1M9 14h2m-9-2 3 3-6 6-3-3zm-1 4 4 4',
    },
    {
      title: '跨专业研发团队',
      desc: '链接科研、工艺、质量与产品开发，提升成果转化效率。',
      path: 'M18 21a8 8 0 0 0-16 0M10 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3',
    },
    {
      title: '实验与检测条件',
      desc: '以设备、实验和质量能力支撑研发结论与生产验证。',
      path: 'M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm4 7-1.9-1.9',
    },
  ]

  const achievementCards = [
    {
      num: '50',
      suffix: '+',
      label: '授权发明专利',
      desc: '',
      path: 'M12 3.85a4.92 4.92 0 0 1 3.05 1.04 4.93 4.93 0 0 1 2.9.12 4.93 4.93 0 0 1 1.26 2.62 4.92 4.92 0 0 1 1.69 2.37 4.93 4.93 0 0 1-.78 2.8 4.94 4.94 0 0 1-.78 2.8 4.92 4.92 0 0 1-2.5 1.49 4.92 4.92 0 0 1-2.17 1.94 4.93 4.93 0 0 1-2.67-.26 4.93 4.93 0 0 1-2.67.26 4.92 4.92 0 0 1-2.17-1.94 4.92 4.92 0 0 1-2.5-1.49 4.94 4.94 0 0 1-.78-2.8 4.93 4.93 0 0 1-.78-2.8 4.92 4.92 0 0 1 1.69-2.37 4.93 4.93 0 0 1 1.26-2.62 4.93 4.93 0 0 1 2.9-.12A4.92 4.92 0 0 1 12 3.85Zm-3 8 2 2 4-4',
      isText: false,
    },
    {
      num: '43',
      suffix: '',
      label: '项省部级以上科技奖励',
      desc: '',
      path: 'M12 2a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM15.477 12.89 17 22l-5-3-5 3 1.523-9.11',
      isText: false,
    },
    {
      num: '成果转化',
      suffix: '',
      label: '',
      desc: '推动科研成果进入中试验证与产品开发阶段。',
      path: 'M3 3h8v8H3zM13 13h8v8h-8zM7 11v2a4 4 0 0 0 4 4h2M11 7h2a4 4 0 0 1 4 4v2',
      isText: true,
    },
    {
      num: '产业应用',
      suffix: '',
      label: '',
      desc: '面向企业需求提供可落地的技术和产品方案。',
      path: 'm16 16 2 2 4-4M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14M7.5 4.27l9 5.15M3.29 7 12 12l8.71-5M12 22V12',
      isText: true,
    },
  ]

  const serviceTags = ['产品研发', '工艺优化', '中试验证', '定制生产', '技术服务']
</script>

<style scoped lang="scss">
  @media (prefers-reduced-motion: reduce) {
    .capability-panel-media img,
    .capability-icon,
    .platform-item,
    .achievement-card {
      transition-duration: 0.01ms !important;
    }
  }

  // ── 响应式 ──
  @include lt-sm {
    .research-strength-page {
      .research-section-head {
        grid-template-columns: 1fr;
        gap: 20px;
        margin-bottom: 32px;

        > p {
          max-width: 680px;
        }
      }

      .capability-panel,
      .platform-layout,
      .achievement-layout,
      .service-layout {
        grid-template-columns: 1fr;
      }

      .capability-panel {
        grid-template-rows: 250px minmax(0, 1fr);
        height: 520px;
        min-height: 0;
        margin-top: 12px;

        .capability-panel-media {
          min-height: 0;
        }

        .capability-panel-copy {
          padding: 24px 22px 26px;

          h3 {
            margin: 9px 0;
            font-size: clamp(22px, 5.6vw, 26px);
          }

          p {
            font-size: clamp(12px, 3.4vw, 13px);
            line-height: 1.75;
          }
        }

        .capability-panel-tags {
          margin-top: 14px;
        }
      }

      .platform-layout {
        gap: 18px;
      }

      .platform-visual {
        min-height: 450px;
      }

      .platform-list {
        grid-template-columns: 1fr;
      }

      .platform-item {
        grid-template-columns: 58px 1fr;
        padding: 23px;
      }

      /* 移动端隐藏 PC 端 tabs + panel */
      .capability-tabs,
      .capability-panel {
        display: none;
      }

      /* 移动端轮播 */
      .capability-carousel {
        --eg-active: 0;
        --eg-inactive: 0;
        --eg-item-shrink: 0;
        --eg-active-basis: 0%;
        --eg-inactive-basis: 0%;

        display: flex;
        gap: 12px;
        width: 100vw;
        padding-inline: 24px;
        padding-bottom: 8px;
        margin-inline: calc((100% - 100vw) / 2);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        :deep(> div) {
          flex: 0 0 calc(100vw - 48px);
          width: calc(100vw - 48px);
          min-width: calc(100vw - 48px);
          scroll-snap-align: center;
          scroll-snap-stop: always;
          transition: none;
        }
      }

      .capability-carousel-card {
        display: flex;
        flex-direction: column;
        min-width: 0;
        overflow: hidden;
        cursor: pointer;
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        transition:
          border-color 320ms var(--ease),
          box-shadow 320ms var(--ease);
      }

      .capability-carousel-media {
        min-width: 0;
        overflow: hidden;

        img {
          width: 100%;
          max-width: 100%;
          height: auto;
          aspect-ratio: 16 / 10;
          object-fit: cover;
        }
      }

      .capability-carousel-copy {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: center;
        min-width: 0;
        padding: 20px;
      }

      .capability-carousel-icon {
        display: grid;
        place-items: center;
        width: 44px;
        height: 44px;
        color: var(--accent);
        background: var(--surface);
        border: 1px solid #dfc4c7;
        border-radius: 12px;
        transition:
          border-color 280ms var(--ease),
          color 280ms var(--ease),
          background 280ms var(--ease);

        svg {
          width: 22px;
          height: 22px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.8;
          stroke-linecap: round;
          stroke-linejoin: round;
        }
      }

      .capability-carousel-card.active .capability-carousel-icon {
        color: var(--white);
        background: var(--accent);
        border-color: var(--accent);
      }

      .capability-carousel-head {
        flex: 1;
        min-width: 0;

        strong {
          display: block;
          font-size: clamp(16px, 4.2vw, 18px);
          font-weight: 650;
        }

        small {
          display: block;
          margin-top: 3px;
          font: 650 9px/1.2 var(--num);
          color: #9b8e90;
          letter-spacing: 0.08em;
        }
      }

      .capability-carousel-copy p {
        flex-basis: 100%;
        margin: 12px 0 0;
        font-size: clamp(12px, 3.2vw, 13px);
        line-height: 1.7;
        color: var(--muted);
      }

      .capability-carousel-copy .capability-panel-tags {
        display: flex;
        flex-basis: 100%;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;

        span {
          padding: 6px 10px;
          font-size: 11px;
          color: var(--muted);
          border: 1px solid #ded3d4;
          border-radius: 99px;
        }
      }

      .pilot-metrics {
        grid-template-columns: 1fr;
        gap: 0;
      }

      .pilot-metric {
        padding: 24px 0;
        border-bottom: 1px solid rgb(255 255 255 / 20%);
        border-left: 0;

        & + .pilot-metric {
          padding: 24px 0;
          border-left: 0;
        }

        &:last-child {
          border-bottom: 0;
        }

        b {
          font-size: clamp(30px, 8vw, 38px);
        }

        span {
          font-size: clamp(12px, 3.2vw, 13px);
        }
      }

      .achievement-cards {
        grid-template-columns: 1fr 1fr;
      }

      .achievement-visual {
        min-height: 390px;
      }

      .service-media {
        order: -1;
        min-height: 380px;
      }
    }
  }

  @include lt-xs {
    .research-strength-page {
      .platform-visual {
        min-height: 390px;

        .platform-caption {
          right: 24px;
          bottom: 24px;
          left: 24px;
        }
      }

      .achievement-cards {
        grid-template-columns: 1fr;
      }

      .achievement-card {
        min-height: 196px;
      }

      .achievement-visual-copy {
        right: 26px;
        bottom: 26px;
        left: 26px;
      }

      .service-media {
        min-height: 300px;
      }
    }
  }

  .research-strength-page {
    text-align: left;
  }

  // ── research-section-head ──
  .research-section-head {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
    gap: 56px;
    align-items: end;
    margin-bottom: 48px;

    h2 {
      max-width: 720px;
      margin: 0;
      font-size: clamp(30px, 3.4vw, 46px);
      font-weight: 650;
      line-height: 1.2;
      letter-spacing: -0.025em;
    }

    > p {
      margin: 0 0 2px;
      font-size: 15px;
      line-height: 1.9;
      color: var(--muted);
    }
  }

  // ── capability-tabs ──
  .capability-tabs {
    position: relative;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    background: transparent;
  }

  .capability-tab {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 218px;
    padding: 24px 12px;
    color: var(--fg);
    text-align: center;
    cursor: pointer;
    background: var(--surface);
    border: 0;
    border-radius: 0;
    transition:
      color 240ms var(--ease),
      background 240ms var(--ease),
      box-shadow 240ms var(--ease),
      transform 240ms var(--ease);

    &:nth-child(1) {
      z-index: 5;
    }

    &:nth-child(2) {
      z-index: 4;
    }

    &:nth-child(3) {
      z-index: 3;
    }

    &:nth-child(4) {
      z-index: 2;
    }

    &:hover {
      color: var(--fg);
    }

    &:focus-visible {
      outline: none;

      .capability-icon {
        outline: 2px solid color-mix(in srgb, var(--accent), white 35%);
        outline-offset: 3px;
      }
    }

    &:not(:last-child)::after {
      position: absolute;
      top: 62px;
      right: -17px;
      width: 34px;
      height: 14px;
      pointer-events: none;
      content: '';
      background: var(--accent);
      opacity: 0.5;
      clip-path: polygon(0 40%, 68% 40%, 68% 4%, 100% 50%, 68% 96%, 68% 60%, 0 60%);
    }

    &:hover .capability-icon {
      border-color: var(--accent);
      box-shadow: 0 10px 22px rgb(183 24 34 / 12%);
      transform: translateY(-3px);
    }

    &.active .capability-icon {
      color: var(--white);
      background: var(--accent);
      border-color: var(--accent);
      box-shadow: 0 12px 26px rgb(183 24 34 / 18%);
    }

    strong {
      font-size: 17px;
      font-weight: 650;
    }

    small {
      margin-top: 7px;
      font: 650 10px/1.2 var(--num);
      color: #9b8e90;
      letter-spacing: 0.08em;
    }
  }

  .capability-tab-copy {
    display: none;
  }

  .capability-carousel {
    display: none;
  }

  .capability-icon {
    display: grid;
    flex: 0 0 68px;
    place-items: center;
    width: 68px;
    height: 68px;
    margin-bottom: 16px;
    color: var(--accent);
    background: var(--surface);
    border: 1px solid #dfc4c7;
    border-radius: 50%;
    transition:
      border-color 240ms var(--ease),
      color 240ms var(--ease),
      background 240ms var(--ease),
      box-shadow 240ms var(--ease),
      transform 240ms var(--ease);

    svg {
      width: 29px;
      height: 29px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  // ── capability-panel ──
  .capability-panel {
    display: grid;
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
    height: 380px;
    min-height: 0;
    margin-top: 24px;
    overflow: hidden;
    color: var(--fg);
    background: var(--surface);
    border: 1px solid #e8dcdd;
    border-radius: var(--radius-md);
    box-shadow: 0 18px 44px rgb(66 29 33 / 8%);

    .capability-panel-media {
      position: relative;
      overflow: hidden;
      background: var(--surface-soft);

      &::before {
        position: absolute;
        inset: 0;
        z-index: 0;
        display: grid;
        visibility: hidden;
        place-items: center;
        color: var(--muted);
        content: '图片加载失败';
        background: var(--surface-soft);
        opacity: 0;
      }

      &::after {
        position: absolute;
        inset: 0;
        z-index: 2;
        pointer-events: none;
        content: '';
        background: linear-gradient(90deg, transparent 72%, rgb(255 255 255 / 12%));
      }

      &.is-error::before {
        visibility: visible;
        opacity: 1;
      }

      img {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition:
          opacity 180ms var(--ease),
          transform 700ms var(--ease);

        &.is-error {
          visibility: hidden;
        }
      }
    }

    &.is-switching .capability-panel-media img {
      opacity: 0.3;
      transform: scale(1.02);
    }

    .capability-panel-copy {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: clamp(38px, 5vw, 68px);

      > span {
        font: 700 11px/1.2 var(--num);
        color: var(--accent);
        letter-spacing: 0.16em;
      }

      h3 {
        margin: 13px 0;
        font-size: clamp(25px, 2.4vw, 34px);
      }

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.95;
        color: var(--muted);
      }
    }

    .capability-panel-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 24px;

      span {
        padding: 7px 10px;
        font-size: 11px;
        color: var(--muted);
        border: 1px solid #ded3d4;
        border-radius: 99px;
      }
    }
  }

  // ── platform-section ──
  .platform-section {
    background: #f7f3f2;
  }

  .platform-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(380px, 0.85fr);
    gap: 30px;
    align-items: stretch;
  }

  .platform-visual {
    position: relative;
    min-height: 540px;
    overflow: hidden;
    background: #ded6d5;
    border-radius: var(--radius-md);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::after {
      position: absolute;
      inset: 38% 0 0;
      pointer-events: none;
      content: '';
      background: linear-gradient(transparent, rgb(31 14 17 / 90%));
    }

    .platform-caption {
      position: absolute;
      right: 38px;
      bottom: 34px;
      left: 38px;
      z-index: 2;
      color: var(--white);

      b {
        display: block;
        margin-bottom: 7px;
        font-size: 25px;
      }

      span {
        font-size: 13px;
        color: rgb(255 255 255 / 68%);
      }
    }
  }

  .platform-list {
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    gap: 15px;
  }

  .platform-item {
    display: grid;
    grid-template-columns: 62px 1fr;
    gap: 20px;
    align-items: center;
    padding: 28px;
    background: var(--surface);
    border: 1px solid #e7dadc;
    border-radius: var(--radius-md);

    h3 {
      margin: 0 0 6px;
      font-size: 18px;
    }

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.65;
      color: var(--muted);
    }
  }

  .platform-item-icon {
    display: grid;
    place-items: center;
    width: 62px;
    height: 62px;
    color: var(--accent);
    background: var(--red-soft);
    border-radius: 17px;

    svg {
      width: 28px;
      height: 28px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  // ── pilot-section ──
  .pilot-section {
    position: relative;
    padding: clamp(84px, 8vw, 122px) 0;
    overflow: hidden;
    color: var(--white);
    background: #7f1018;
    isolation: isolate;

    &::before {
      position: absolute;
      inset: 0;
      z-index: -2;
      content: '';
      background: url('/images/company/strength/pilot-base.jpg') center / cover no-repeat;
    }

    &::after {
      position: absolute;
      inset: 0;
      z-index: -1;
      content: '';
      background: linear-gradient(
        90deg,
        rgb(91 10 17 / 97%),
        rgb(165 20 30 / 84%) 52%,
        rgb(59 7 12 / 38%)
      );
    }

    .research-kicker {
      color: #ffb0b5;
    }

    h2 {
      margin: 0;
      font-size: clamp(32px, 3.8vw, 50px);
      line-height: 1.2;
      letter-spacing: -0.025em;
    }
  }

  .pilot-copy {
    max-width: 620px;

    > p {
      margin: 20px 0 0;
      font-size: 15px;
      line-height: 2;
      color: rgb(255 255 255 / 72%);
    }
  }

  .pilot-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 56px;
    border-top: 1px solid rgb(255 255 255 / 26%);
  }

  .pilot-metric {
    padding: 32px 24px 0 0;

    & + .pilot-metric {
      padding-left: 34px;
      border-left: 1px solid rgb(255 255 255 / 22%);
    }

    b {
      display: block;
      font: 700 clamp(38px, 4vw, 58px) / 1 var(--num);
      letter-spacing: -0.03em;
    }

    sup {
      font-size: 0.42em;
      vertical-align: super;
    }

    span {
      display: block;
      margin-top: 12px;
      font-size: 13px;
      color: rgb(255 255 255 / 68%);
    }
  }

  // ── achievements ──
  .achievement-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.06fr) minmax(0, 0.94fr);
    gap: 22px;
  }

  .achievement-visual {
    position: relative;
    min-height: 470px;
    overflow: hidden;
    color: var(--white);
    background: #322427;
    border-radius: var(--radius-md);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::after {
      position: absolute;
      inset: 0;
      z-index: 1;
      content: '';
      background: linear-gradient(0deg, rgb(39 15 18 / 96%), rgb(39 15 18 / 2%) 68%);
    }

    .media-fallback {
      z-index: 0;
    }

    .achievement-visual-copy {
      position: absolute;
      right: 40px;
      bottom: 38px;
      left: 40px;
      z-index: 2;

      span {
        font: 700 11px/1.2 var(--num);
        color: #f18b92;
        letter-spacing: 0.14em;
      }

      h3 {
        margin: 10px 0 8px;
        font-size: 27px;
      }

      p {
        margin: 0;
        font-size: 13px;
        color: rgb(255 255 255 / 68%);
      }
    }
  }

  .achievement-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }

  .achievement-card {
    min-height: 226px;
    padding: 30px;
    color: var(--fg);
    cursor: default;
    background: var(--surface);
    border: 1px solid #e9dcdd;
    border-radius: var(--radius-md);

    .achievement-icon {
      display: grid;
      place-items: center;
      width: 48px;
      height: 48px;
      margin-bottom: 33px;
      color: var(--accent);
      background: var(--red-soft);
      border-radius: 14px;

      svg {
        width: 23px;
        height: 23px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    }

    b {
      display: block;
      font: 700 38px/1 var(--num);

      &.text {
        font: 650 19px/1.3 var(--font);
      }
    }

    span {
      display: block;
      margin-top: 8px;
      font-size: 13px;
      color: var(--muted);
    }

    p {
      margin: 10px 0 0;
      font-size: 13px;
      line-height: 1.75;
      color: var(--muted);
    }
  }

  // ── service-section ──
  .service-section {
    background: var(--surface-soft);
  }

  .service-layout {
    display: grid;
    grid-template-columns: minmax(380px, 0.9fr) minmax(0, 1.1fr);
    overflow: visible;
    color: var(--fg);
    background: transparent;
  }

  .service-copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: clamp(42px, 5vw, 70px);

    .research-kicker {
      color: var(--accent);
    }

    h2 {
      margin: 0;
      font-size: clamp(29px, 3vw, 42px);
      line-height: 1.25;
    }

    > p {
      margin: 18px 0 0;
      font-size: 14px;
      line-height: 1.95;
      color: var(--muted);
    }

    .service-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 9px;
      margin-top: 28px;

      span {
        padding: 8px 12px;
        font-size: 11px;
        color: var(--muted);
        background: var(--surface);
        border: 1px solid #ded3d4;
        border-radius: 99px;
      }
    }
  }

  .service-media {
    min-height: 450px;
    overflow: hidden;
    border-radius: var(--radius-md);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
</style>
